import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import NotFoundPage from "@/pages/NotFound";
import FilterBar from "./components/FilterBar";
import {
  getCategoryBySlug,
  getByCategory,
  filterNews,
  sortNews,
  collectTags,
  type SortKey,
} from "@/utils/news";
import { regions, timeRanges } from "@/mocks/news";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categorySlug = "" } = useParams();
  const category = getCategoryBySlug(categorySlug);

  const [region, setRegion] = useState("全部");
  const [timeRange, setTimeRange] = useState("全部");
  const [tag, setTag] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);

  const base = useMemo(() => getByCategory(categorySlug), [categorySlug]);
  const tagOptions = useMemo(() => collectTags().slice(0, 12), []);

  useEffect(() => {
    setRegion("全部");
    setTimeRange("全部");
    setTag("全部");
    setSort("latest");
    setPage(1);
  }, [categorySlug]);

  const filtered = useMemo(
    () => sortNews(filterNews(base, { region, timeRange, tag }), sort),
    [base, region, timeRange, tag, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [region, timeRange, tag, sort]);

  if (!category) return <NotFoundPage />;

  const reset = () => {
    setRegion("全部");
    setTimeRange("全部");
    setTag("全部");
    setSort("latest");
    setPage(1);
  };

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-5 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类" },
            { label: category.name },
          ]}
        />

        {/* Category header */}
        <div className="mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 p-5 md:p-7 text-background-50">
          <div className="flex items-start gap-4">
            <span className="hidden sm:flex w-14 h-14 shrink-0 items-center justify-center rounded-lg bg-background-50/15 text-3xl">
              <i className={category.icon} />
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-black">{category.name}</h1>
              <p className="mt-2 text-sm md:text-base text-background-100 leading-relaxed max-w-3xl">
                {category.desc}
              </p>
              <p className="mt-3 text-xs text-background-200">
                共收录 <span className="font-bold text-accent-300">{base.length}</span> 条相关资讯
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-5">
          <FilterBar
            regions={regions}
            timeRanges={timeRanges}
            tagOptions={tagOptions}
            region={region}
            timeRange={timeRange}
            tag={tag}
            sort={sort}
            onRegion={setRegion}
            onTimeRange={setTimeRange}
            onTag={setTag}
            onSort={(v) => setSort(v as SortKey)}
            onReset={reset}
          />
        </div>

        {/* Result info */}
        <div className="mt-4 flex items-center justify-between text-sm text-foreground-500">
          <span>
            共找到 <span className="font-bold text-primary-600">{filtered.length}</span> 条结果
          </span>
          <span className="hidden sm:inline">
            第 {page} / {totalPages} 页
          </span>
        </div>

        {/* List */}
        {paged.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3 md:gap-4">
            {paged.map((article) => (
              <NewsCard key={article.id} article={article} variant="horizontal" />
            ))}
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl">
              <i className="ri-search-eye-line" />
            </span>
            <p className="mt-4 text-foreground-700 font-medium">暂无相关内容</p>
            <p className="mt-1 text-sm text-foreground-400">尝试重置筛选条件，或浏览其他分类</p>
            <button
              type="button"
              onClick={reset}
              className="mt-5 h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        )}

        <Pagination current={page} total={totalPages} onChange={setPage} />

        {/* Back to home */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line" />
            返回首页浏览更多资讯
          </Link>
        </div>
      </div>
    </Layout>
  );
}